import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTrashAlt } from 'react-icons/fa';
import i18n from '@/utils/i18n';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import LoadingSpinner from '@/components/LoadingSpinner';
import { ContentSection } from '@/components/Layout/ContentSection';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useBrandsQuery } from '@/graphql/queries/brands.generated';
import { Routes } from '@/utils/routes';
import { getImageUrl } from '@/utils/imageUrl';

export default function LegacyBrandSearch() {
  useDocumentTitle('PageTitles:partnerMerchants');

  const [keyword, setKeyword] = useState('');
  const [includeRemoved, setIncludeRemoved] = useState(false);

  const { data, loading } = useBrandsQuery({
    fetchPolicy: 'cache-and-network',
    variables: { admin_show_all: true },
  });

  const brands = useMemo(() => {
    const all = data?.brands ?? [];
    const lowerKeyword = keyword.trim().toLowerCase();
    return all
      .filter(
        (brand): brand is NonNullable<typeof brand> =>
          brand !== null && brand !== undefined,
      )
      .filter((brand) => includeRemoved || !brand.permanently_removed)
      .filter(
        (brand) =>
          !lowerKeyword ||
          (brand.name ?? '').toLowerCase().includes(lowerKeyword),
      )
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [data, includeRemoved, keyword]);

  return (
    <ContentSection title={i18n.t('LegacyBrands:title')} data-use-bs={true}>
      <div className="alert alert-warning mt-2">
        {i18n.t('LegacyBrands:legacyNotice')}
      </div>

      <div className="input-group mb-2 mt-3">
        <div className="input-group-text">{i18n.t('Common:search')}</div>
        <input
          type="text"
          name="legacy-brand-search"
          className="form-control"
          autoComplete="off"
          placeholder={i18n.t('Common:search')}
          onChange={(e) => setKeyword(e.target.value)}
          value={keyword}
        />
      </div>

      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2 tw:mb-2">
        <Checkbox
          id="legacy-include-removed"
          checked={includeRemoved}
          onCheckedChange={(checked) => setIncludeRemoved(checked === true)}
        />
        <Label htmlFor="legacy-include-removed">
          {i18n.t('PartnerSearch:showDisabledRemovedBrands')}
        </Label>
      </div>

      <ScrollArea className="tw:h-[500px] tw:rounded tw:border">
        {loading && (
          <div className="p-3">
            <LoadingSpinner />
          </div>
        )}
        {!loading && brands.length === 0 && (
          <div className="text-muted ps-3 pt-2">
            {i18n.t('Common:noResults')}
          </div>
        )}
        {!loading && brands.length > 0 && (
          <div className="tw:flex tw:flex-col tw:gap-1 tw:p-2">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                to={Routes.legacyBrandEdit(brand.id)}
                className={cn(
                  'tw:flex tw:flex-row tw:items-center tw:gap-3 tw:rounded tw:px-2 tw:py-2 tw:no-underline tw:text-inherit tw:hover:bg-muted',
                  brand.permanently_removed && 'tw:opacity-40',
                )}
              >
                <Avatar className="tw:size-8">
                  <AvatarImage src={getImageUrl(30, brand.logo) as string} />
                  <AvatarFallback>{brand.name?.[0] ?? '?'}</AvatarFallback>
                </Avatar>
                <span className="tw:font-medium">{brand.name}</span>
                {brand.permanently_removed && (
                  <FaTrashAlt
                    title={i18n.t('Merchants:storeRemovedUsersWarning')}
                    className="tw:text-destructive"
                  />
                )}
              </Link>
            ))}
          </div>
        )}
      </ScrollArea>
    </ContentSection>
  );
}
